import React, { useState, useEffect } from 'react';
import { BREAKPOINT_TABLET, BREAKPOINT_DESKTOP } from '../../constants/layout';
import { Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

function useBreakpoint() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    isMobile: width < BREAKPOINT_TABLET,
    isTablet: width >= BREAKPOINT_TABLET && width < BREAKPOINT_DESKTOP,
    isDesktop: width >= BREAKPOINT_DESKTOP,
  };
}

/**
 * Generic three-pane directory layout.
 * Props:
 *   filters    - React node for the filter sidebar content
 *   list       - React node for the list/card column
 *   detail     - React node for the detail pane (or null for empty state)
 *   emptyState - React node shown when detail is null
 *   title      - string, page title (e.g. "OPEN REQUESTS")
 *   count      - number, shown as "{n} available"
 *   sortControl - React node for the sort dropdown
 *   mobileContent - React node for the full mobile layout (unchanged behavior)
 */
export default function DirectoryLayout({ filters, list, detail, emptyState, title, count, sortControl, mobileContent }) {
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Desktop filter pane: auto-collapse when a detail is open, auto-expand when none.
  // Manual toggle persists until the detail state changes again (useEffect resets it).
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  useEffect(() => {
    setFilterCollapsed(!!detail);
  }, [detail]);

  // Mobile: render existing layout unchanged
  if (isMobile) return mobileContent;

  // Desktop three-pane
  if (isDesktop) {
    return (
      <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
        {/* Filter pane (collapsible) */}
        {filterCollapsed ? (
          <aside
            className="shrink-0 flex flex-col items-center pt-4"
            style={{ width: 44, borderRight: '1px solid var(--s8-b1-solid)' }}
            data-testid="filter-pane-collapsed"
          >
            <button
              onClick={() => setFilterCollapsed(false)}
              className="p-2 rounded hover:bg-[var(--s8-bg-card)]"
              title="Show filters"
              aria-label="Show filters"
              data-testid="filter-pane-expand-btn"
            >
              <ChevronRight className="h-5 w-5" style={{ color: 'var(--t2)' }} />
            </button>
          </aside>
        ) : (
          <aside
            className="shrink-0 overflow-y-auto"
            style={{ width: 280, borderRight: '1px solid var(--s8-b1-solid)', scrollbarWidth: 'thin' }}
            data-testid="filter-pane-expanded"
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-1">
                <h1 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: 'clamp(1.875rem, 1.875rem + 1vw, 2.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em', textWrap: 'balance', color: 'var(--t0)' }}>{title}</h1>
                <button
                  onClick={() => setFilterCollapsed(true)}
                  className="p-1 rounded hover:bg-[var(--s8-bg-card)] shrink-0"
                  title="Hide filters"
                  aria-label="Hide filters"
                  data-testid="filter-pane-collapse-btn"
                >
                  <ChevronLeft className="h-5 w-5" style={{ color: 'var(--t2)' }} />
                </button>
              </div>
              <p className="mb-5" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px', color: 'var(--t3)' }}>{count} available</p>
              {filters}
            </div>
          </aside>
        )}

        {/* List pane */}
        <div className="shrink-0 flex flex-col overflow-hidden" style={{ width: 468, borderRight: '1px solid var(--s8-b1-solid)' }}>
          <div className="shrink-0 px-4 py-3" style={{ borderBottom: '1px solid var(--s8-b1-solid)' }}>
            {sortControl}
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col" style={{ scrollbarWidth: 'thin', gap: '12px' }}>
            {list}
          </div>
        </div>

        {/* Detail pane */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          {detail || emptyState}
        </div>
      </div>
    );
  }

  // Tablet two-pane
  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--s8-b1-solid)' }}>
        <div className="flex items-baseline gap-3">
          <h1 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: 'clamp(1.875rem, 1.875rem + 1vw, 2.25rem)', lineHeight: 1.1, letterSpacing: '-0.02em', textWrap: 'balance', color: 'var(--t0)' }}>{title}</h1>
          <span style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px', color: 'var(--t3)' }}>{count} available</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setDrawerOpen(true)}
            className="browse-chip flex items-center gap-1.5"
            data-testid="tablet-filter-btn"
          >
            <Filter className="h-3 w-3" />Filters
          </button>
          {sortControl}
        </div>
      </div>

      {/* Two panes */}
      <div className="flex" style={{ height: 'calc(100vh - 56px - 57px)' }}>
        <div className="shrink-0 overflow-y-auto px-3 py-3 flex flex-col" style={{ width: 408, borderRight: '1px solid var(--s8-b1-solid)', scrollbarWidth: 'thin', gap: '12px' }}>
          {list}
        </div>
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          {detail || emptyState}
        </div>
      </div>

      {/* Filter drawer */}
      {drawerOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="fixed top-0 left-0 z-50 h-full overflow-y-auto pl-[env(safe-area-inset-left,0px)] pb-[env(safe-area-inset-bottom,0px)]" style={{ width: 320, background: 'var(--s8-bg-base)', borderRight: '1px solid var(--s8-b1-solid)' }}>
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--s8-b1-solid)' }}>
              <span style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '20px', color: 'var(--t0)' }}>Filters</span>
              <button onClick={() => setDrawerOpen(false)} className="p-1"><X className="h-5 w-5" style={{ color: 'var(--t2)' }} /></button>
            </div>
            <div className="p-4">{filters}</div>
          </aside>
        </>
      )}
    </div>
  );
}

export { useBreakpoint };
